import { ATTRIBUTES, changedStats, derivedStatsOf, type AttributeSpend, type Rules, type StatChange } from "odyssey-shared";

/**
 * The Character tab's pending allocation (ADR-138's amendment of 2026-10-31, decisions 3 and 5): + and - move points
 * in a local choice that is never sent until Confirm, which sends the whole choice as one `spendAttributes`. Pure
 * functions over plain data; the window holds the state.
 */

/**
 * What the page last heard from the server for its own character: the `attributes` frame's fields.
 */
export interface AttributesFrame {
  readonly values: readonly number[];
  readonly effective: readonly number[];
  /** The effects' flat points per attribute before the clamp, which the preview adds as the server does. */
  readonly bonus: readonly number[];
  readonly points: number;
  readonly background: string | null;
  readonly respec: "free" | "shrine";
}

/**
 * Pending points per attribute, in `ATTRIBUTES` order.
 */
export type Pending = readonly number[];

/**
 * No point pending.
 */
export const NO_PENDING: Pending = Object.freeze(ATTRIBUTES.map(() => 0));

/**
 * The points a pending choice holds.
 *
 * @param pending The choice.
 * @returns Their sum.
 */
export function pendingTotal(pending: Pending): number {
  return pending.reduce((sum, value) => sum + value, 0);
}

/**
 * Whether + may add a point to an attribute: points remain and the stored value with its pending points stays within
 * the cap.
 *
 * @param frame The last frame.
 * @param pending The choice.
 * @param index The attribute's place in `ATTRIBUTES`.
 * @param cap `attribute_cap`.
 * @returns True when a point may be added.
 */
export function canAdd(frame: AttributesFrame, pending: Pending, index: number, cap: number): boolean {
  return pendingTotal(pending) < frame.points && (frame.values[index] as number) + (pending[index] as number) < cap;
}

/**
 * A choice with one point added, or the same choice when none may be.
 *
 * @param frame The last frame.
 * @param pending The choice.
 * @param index The attribute's place.
 * @param cap `attribute_cap`.
 * @returns The new choice.
 */
export function addPoint(frame: AttributesFrame, pending: Pending, index: number, cap: number): Pending {
  return canAdd(frame, pending, index, cap) ? pending.map((value, at) => (at === index ? value + 1 : value)) : pending;
}

/**
 * A choice with one point taken back, or the same choice when the attribute has none pending.
 *
 * @param pending The choice.
 * @param index The attribute's place.
 * @returns The new choice.
 */
export function removePoint(pending: Pending, index: number): Pending {
  return (pending[index] as number) > 0 ? pending.map((value, at) => (at === index ? value - 1 : value)) : pending;
}

/**
 * The `spendAttributes` batch a choice sends: one entry per attribute with points pending.
 *
 * @param pending The choice.
 * @returns The batch, empty for none.
 */
export function spendsOf(pending: Pending): AttributeSpend[] {
  return ATTRIBUTES.flatMap((attribute, index) => ((pending[index] as number) > 0 ? [[attribute, pending[index] as number] as const] : []));
}

/**
 * The choice that survives a new frame: kept while the frame still holds the points it needs, dropped otherwise (a
 * level up keeps it; a spend that committed or a respec drops it).
 *
 * @param before The frame the choice was made on, or null.
 * @param after The new frame.
 * @param pending The choice.
 * @returns The choice to keep.
 */
export function pendingAfter(before: AttributesFrame | null, after: AttributesFrame, pending: Pending): Pending {
  if (before === null || pendingTotal(pending) > after.points || after.values.some((value, index) => value !== before.values[index])) {
    return NO_PENDING;
  }
  return pending;
}

/**
 * The preview panel's rows: the derived stats a choice changes, before and after, from the server's own formulas and
 * its clamp: the stored values with the pending points and the effects' flat points, through `effectiveAttribute`.
 *
 * @param frame The last frame.
 * @param pending The choice.
 * @param level The character's level.
 * @param rules The rules.
 * @returns The changed stats, empty when nothing changes.
 */
export function previewOf(frame: AttributesFrame, pending: Pending, level: number, rules: Rules): StatChange[] {
  const before = derivedStatsOf(level, frame.values, frame.bonus, rules);
  const after = derivedStatsOf(level, frame.values.map((value, index) => value + (pending[index] ?? 0)), frame.bonus, rules);
  return changedStats(before, after);
}
